import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, MapPin, Phone, Mail, Clock, ArrowRight, LogIn } from "lucide-react";
import Logo from "@/components/Logo";
import { WhatsAppFloat, WhatsAppIcon, useCompanyContact } from "@/components/WhatsApp";
import { waLink } from "@/lib/format";

const NAV = [
  { to: "/", label: "Início", end: true },
  { to: "/sobre", label: "Sobre" },
  { to: "/servicos", label: "Serviços" },
  { to: "/maquinas", label: "Máquinas" },
  { to: "/galeria", label: "Galeria" },
  { to: "/contato", label: "Contato" },
];

export const CONTACT = {
  phone: "(19) 99772-1406",
  whatsapp: "(19) 99772-1406",
  email: "alphametais.cnc@gmail.com",
  address: "Rua 26, 956 — Jardim Mirassol, Rio Claro/SP · CEP 13.503-130",
  hours: "Seg. a Sex. · 07h30 às 17h00",
};

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

function QuoteButton({ mobile = false }) {
  return (
    <Link
      to="/painel/orcamentos/novo"
      data-testid={mobile ? "mobile-nav-quote-button" : "nav-quote-button"}
      className={`inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors ${
        mobile ? "justify-center px-5 py-3" : "px-5 py-2.5"
      }`}
    >
      Solicitar Orçamento <ArrowRight className="w-4 h-4" />
    </Link>
  );
}

function LoginButton({ mobile = false }) {
  return (
    <Link
      to="/login"
      data-testid={mobile ? "mobile-nav-client-area-button" : "nav-client-area-button"}
      className={`inline-flex items-center gap-2 border border-zinc-600 hover:border-orange-500 hover:text-orange-400 text-zinc-200 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors ${
        mobile ? "justify-center px-5 py-3" : "px-4 py-2.5"
      }`}
    >
      <LogIn className="w-4 h-4" /> Área Administrativa
    </Link>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    fn();
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <header
      data-testid="public-header"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-zinc-950/90 backdrop-blur-md border-zinc-800" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" aria-label="Alpha Metais - Início">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${slug(n.label)}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                  isActive ? "text-orange-500" : "text-zinc-400 hover:text-zinc-100"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <div className="ml-3 flex items-center gap-2.5">
            <LoginButton />
            <QuoteButton />
          </div>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-zinc-200 p-2"
          aria-label="Abrir menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-zinc-950/98 backdrop-blur-md border-t border-zinc-800" data-testid="mobile-nav">
          <nav className="px-4 py-4 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`mobile-nav-${slug(n.label)}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm font-medium uppercase tracking-wider border-b border-zinc-900 ${
                    isActive ? "text-orange-500" : "text-zinc-400"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="mt-4 flex flex-col gap-2.5">
              <QuoteButton mobile />
              <LoginButton mobile />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const contact = useCompanyContact();
  const waDisplay = contact?.whatsapp || CONTACT.whatsapp;
  const waHref = waLink(waDisplay, "Olá! Gostaria de falar com a Alpha Metais.");
  return (
    <footer className="bg-zinc-950 border-t border-zinc-800" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-3">
        <div>
          <Logo />
          <p className="mt-5 text-sm text-zinc-500 leading-relaxed max-w-xs">
            Usinagem CNC, corte plasma e laser, gravação laser, solda e fabricação
            de peças sob desenho. Do projeto à peça acabada, com qualidade e prazo garantidos.
          </p>
        </div>
        <div>
          <h3 className="display text-lg font-bold uppercase tracking-wider text-zinc-200">Navegação</h3>
          <ul className="mt-4 space-y-2.5">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-sm text-zinc-500 hover:text-orange-400 transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/painel/orcamentos/novo" className="text-sm text-orange-500 hover:text-orange-400 transition-colors font-medium">
                Solicitar Orçamento
              </Link>
            </li>
            <li>
              <Link to="/acompanhar" className="text-sm text-zinc-500 hover:text-orange-400 transition-colors">
                Acompanhar Orçamento
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h3 className="display text-lg font-bold uppercase tracking-wider text-zinc-200">Contato</h3>
          <ul className="mt-4 space-y-3 text-sm text-zinc-500">
            <li className="flex items-start gap-2.5"><MapPin className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />{CONTACT.address}</li>
            <li className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-orange-500 shrink-0" />{CONTACT.phone} · <a href={waHref} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link" className="text-orange-400 hover:text-orange-300 transition-colors underline-offset-2 hover:underline">{waDisplay} (WhatsApp)</a></li>
            <li className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-orange-500 shrink-0" />{CONTACT.email}</li>
            <li className="flex items-center gap-2.5"><Clock className="w-4 h-4 text-orange-500 shrink-0" />{CONTACT.hours}</li>
            <li className="pt-1.5">
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-cta"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs font-semibold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors">
                <WhatsAppIcon className="w-4 h-4" /> Falar no WhatsApp
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-zinc-900 py-5 text-center text-xs text-zinc-600 mono uppercase tracking-widest">
        © {new Date().getFullYear()} Alpha Metais · Usinagem CNC · Corte · Solda · Fabricação
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
